import React, { useState } from 'react';
import { FaEdit, FaRedo } from 'react-icons/fa';
import styled from 'styled-components';
import { PRIMARY_TEXT_COLOR } from '../../constants';
import { FaForward, FaSpinner } from 'react-icons/fa6';
import usePlayTextToAudio from '../../hooks/chat/usePlayTextToAudio';
import { AiOutlineSound } from 'react-icons/ai';
import { ChatMessage } from '../../types';
import { useLoggedInUserContext } from '../../context/LoggedInUserContextProvider';
import ReactAudioPlayer from 'react-audio-player';

interface ActionButtonsInMessageProps {
  handleRetryMessage: () => void;
  handleContinue: () => void;
  handleEditMessageBotMessage: () => void;
  message: ChatMessage;
}

const processMessage = (message: ChatMessage) => {
  return message.content.replace(/\*.*?\*/g, '');
};

const ActionButtonsInMessage: React.FC<ActionButtonsInMessageProps> = ({
  handleRetryMessage,
  handleContinue,
  handleEditMessageBotMessage,
  message,
}) => {
  const { loggedInUser } = useLoggedInUserContext();
  const { 
    playTextToAudio, 
    audioUrl, 
    audioPlayerRef, 
    isPlaying,
    error 
  } = usePlayTextToAudio();
  const [loadingAudio, setLoadingAudio] = useState(false);

  const shouldShowAudioButton = loggedInUser?.username === 'alpo';

  const textToPlay = processMessage(message);
  const handlePlayingTextToAudio = async () => {
    if (loadingAudio || isPlaying) {
      // Stop the audio process
      if (audioPlayerRef.current?.audioEl.current) {
        audioPlayerRef.current.audioEl.current.pause();
        audioPlayerRef.current.audioEl.current.currentTime = 0;
      }
      setLoadingAudio(false);
      return;
    }
    
    setLoadingAudio(true);
    try {
      await playTextToAudio(textToPlay);
    } catch (err) {
      console.error('Failed to play audio:', err);
    } finally {
      setLoadingAudio(false);
    }
  };

  // Log errors (for debugging)
  React.useEffect(() => {
    if (error) {
      console.error('Audio player error:', error);
    }
  }, [error]);

  return (
    <Container>
      <ActionButtonInMessageBubble onClick={handleEditMessageBotMessage}>
        <FaEdit />
      </ActionButtonInMessageBubble>
      <ActionButtonInMessageBubble onClick={handleRetryMessage}>
        <FaRedo />
      </ActionButtonInMessageBubble>
      <ActionButtonInMessageBubble onClick={handleContinue}>
        <FaForward />
      </ActionButtonInMessageBubble>
      {shouldShowAudioButton && (
        <ActionButtonInMessageBubble onClick={handlePlayingTextToAudio}>
          {loadingAudio || isPlaying ? (
            <FaSpinner className={isPlaying ? 'spin' : ''} />
          ) : (
            <AiOutlineSound />
          )}
        </ActionButtonInMessageBubble>
      )}

      {/* Audio player with better handling for the URL */}
      <HiddenAudioPlayer>
        <ReactAudioPlayer
          ref={audioPlayerRef}
          src={audioUrl || undefined}
          autoPlay={false}
          preload="auto"
        />
      </HiddenAudioPlayer>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 6px;
  margin-right: 0px;
  gap: 16px;
`;

const HiddenAudioPlayer = styled.div`
  display: none;
`;

export const ActionButtonInMessageBubble = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 4px 12px;
  border: none;
  border-radius: 16px;

  cursor: pointer;
  transition: transform 0.2s;
  &:hover {
    transform: scale(1.05);
    color: ${PRIMARY_TEXT_COLOR};
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

export default ActionButtonsInMessage;
